/**
 * useUrlState — keeps React state in sync with URL query parameters.
 *
 * This is the foundation of deep-linkable reports.
 * The URL IS the report — every config change updates the URL,
 * and every URL change updates the UI state.
 *
 * Like Firebase Console:
 *   /product?table=events&events=purchase,view&start=2024-01-01&interval=d
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

type Serializer<T> = {
  encode: (value: T) => string;
  decode: (value: string) => T;
};

// ─── Built-in serializers ───────────────────────────────────────────────────

export const serializers = {
  string: {
    encode: (v: string) => v,
    decode: (v: string) => v,
  } satisfies Serializer<string>,

  number: {
    encode: (v: number) => String(v),
    decode: (v: string) => Number(v),
  } satisfies Serializer<number>,

  boolean: {
    encode: (v: boolean) => (v ? 'true' : 'false'),
    decode: (v: string) => v === 'true',
  } satisfies Serializer<boolean>,

  /** Comma-separated array */
  array: {
    encode: (v: string[]) => v.join(','),
    decode: (v: string) => v.split(',').filter(Boolean),
  } satisfies Serializer<string[]>,

  /** JSON-encoded object (base64url) */
  json: <T>() => ({
    encode: (v: T) => {
      try {
        return btoa(JSON.stringify(v));
      } catch {
        return '';
      }
    },
    decode: (v: string) => {
      try {
        return JSON.parse(atob(v)) as T;
      } catch {
        return undefined as T;
      }
    },
  }),
};

// ─── useUrlParam — single parameter ────────────────────────────────────────

/**
 * Sync a single URL query parameter with React state.
 *
 * @example
 * const [table, setTable] = useUrlParam('table', 'default_table');
 * const [events, setEvents] = useUrlParam('events', [], serializers.array);
 */
export function useUrlParam<T = string>(
  key: string,
  defaultValue: T,
  serializer?: Serializer<T>,
): [T, (value: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const ser = serializer || (serializers.string as unknown as Serializer<T>);

  const value = useMemo(() => {
    const raw = searchParams.get(key);
    if (raw === null || raw === '') return defaultValue;
    return ser.decode(raw);
  }, [searchParams, key, defaultValue, ser]);

  const setValue = useCallback(
    (newValue: T) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const encoded = ser.encode(newValue);
          const defaultEncoded = ser.encode(defaultValue);

          // Remove param if it equals the default (keeps URLs clean)
          if (encoded === defaultEncoded || encoded === '') {
            next.delete(key);
          } else {
            next.set(key, encoded);
          }
          return next;
        },
        { replace: true },
      );
    },
    [key, defaultValue, ser, setSearchParams],
  );

  return [value, setValue];
}

// ─── useUrlState — bulk state management ────────────────────────────────────

/**
 * Get all current URL parameters as a flat object.
 * Useful for sending the full config to the API.
 */
export function useUrlParams(): Record<string, string> {
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    const params: Record<string, string> = {};
    for (const [key, value] of searchParams.entries()) {
      params[key] = value;
    }
    return params;
  }, [searchParams]);
}

/**
 * Bulk-set URL parameters from a config object.
 * Replaces all current params with the new ones.
 */
export function useSetUrlParams(): (params: Record<string, string>) => void {
  const [, setSearchParams] = useSearchParams();

  return useCallback(
    (params: Record<string, string>) => {
      const next = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null && value !== '') {
          next.set(key, value);
        }
      }
      setSearchParams(next, { replace: true });
    },
    [setSearchParams],
  );
}

// ─── useBulkUrlState — typed batch state from URL ───────────────────────────

export interface BulkUrlStateOptions {
  /** Keys that should be parsed as numbers */
  numberKeys?: string[];
  /** Keys that should be parsed as booleans */
  booleanKeys?: string[];
  /** Keys that should be parsed as comma-separated arrays */
  arrayKeys?: string[];
  /** Keys to reset when any other key changes (e.g., 'page') */
  resetOnChange?: string[];
  /** Use { replace: true } for setSearchParams (default: false) */
  replace?: boolean;
  /** Keys to always preserve (even on reset) with their value */
  preserveOnReset?: Record<string, string>;
}

/**
 * Bulk URL state hook — the foundation for section state hooks.
 *
 * Reads all config from URL search params with typed defaults,
 * provides an `update(patch)` that only writes non-default values,
 * and a `reset()` that clears all params.
 *
 * @example
 * const { state, update, reset, searchParams } = useBulkUrlState(DEFAULTS, { numberKeys: ['page'] });
 */
export function useBulkUrlState<T extends Record<string, any>>(
  defaults: T,
  options?: BulkUrlStateOptions,
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    numberKeys = [],
    booleanKeys = [],
    arrayKeys = [],
    resetOnChange = [],
    replace = false,
    preserveOnReset,
  } = options ?? {};

  const state = useMemo<T>(() => {
    const result = {} as Record<string, unknown>;
    for (const [key, defaultVal] of Object.entries(defaults)) {
      const raw = searchParams.get(key);
      if (arrayKeys.includes(key)) {
        result[key] = raw ? raw.split(',').filter(Boolean) : defaultVal;
      } else if (booleanKeys.includes(key)) {
        result[key] = raw === 'true';
      } else if (numberKeys.includes(key)) {
        result[key] = raw ? Number(raw) : defaultVal;
      } else {
        result[key] = raw ?? defaultVal;
      }
    }
    return result as T;
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = useCallback(
    (patch: Partial<T>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);

          for (const [key, value] of Object.entries(patch)) {
            const defaultVal = defaults[key];
            const encoded = Array.isArray(value)
              ? value.join(',')
              : typeof value === 'boolean'
                ? String(value)
                : String(value ?? '');
            const defaultEncoded = Array.isArray(defaultVal)
              ? (defaultVal as string[]).join(',')
              : String(defaultVal ?? '');

            if (encoded === defaultEncoded || encoded === '' || encoded === 'false') {
              next.delete(key);
            } else {
              next.set(key, encoded);
            }
          }

          // Reset specified keys when other keys change
          if (resetOnChange.length > 0) {
            const changedKeys = Object.keys(patch);
            const hasNonResetChange = changedKeys.some((k) => !resetOnChange.includes(k));
            if (hasNonResetChange) {
              for (const k of resetOnChange) {
                if (!(k in patch)) next.delete(k);
              }
            }
          }

          return next;
        },
        replace ? { replace: true } : undefined,
      );
    },
    [setSearchParams, replace], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const reset = useCallback(() => {
    setSearchParams(preserveOnReset ?? {}, replace ? { replace: true } : undefined);
  }, [setSearchParams, replace, preserveOnReset]);

  return { state, update, reset, searchParams };
}
